import { winnerContract } from '@daymade/api-contracts';
import { throwError } from '@daymade/common-lib/errors';
import { ClientInferResponseBody } from '@ts-rest/core';
import GenericModal from 'components/GenericModal';
import ImageWithFallback from 'components/ImageWithFallback';
import ChoosePrizeModal from 'components/prizes/ChoosePrizeModal';
import RoundedButton, { ColorStyle } from 'components/RoundedButton';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import Router from 'next/router';
import { useState } from 'react';
import { winnerApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';
import {
  getDisplayPrizeTierCode,
  getPrizeTierBackground,
  getPrizeTierIcon,
} from './prizeTierCodeImageMap';
import styles from './UnclaimedPrize.module.css';

type PrizeType =
  | 'MAIN_DRAW'
  | 'MONTHLY_GIVEAWAY'
  | 'TOKEN_TOWN'
  | 'CONSOLATION_PRIZE'
  | 'LEADERBOARD_PRIZE';

type TPrizeDetails = ClientInferResponseBody<
  typeof winnerContract.auth.getPrizeBySlugForRedemption,
  200
>;

interface UnclaimedPrizeProps {
  className?: string;
  prizeWinId: string;
  prizeTierCode: string;
  prizeImageLocation: string;
  prizeType: PrizeType;
  drawName: string;
  drawId?: number;
  prizeName?: string;
  createdAt: Date | string;
  expiresAt: Date | string;
  drawPrizeSlug?: string;
  onPrizeClaimed?: () => void;
  showExpiry?: boolean;
  consolationPrizeClaimButtonShowsPopup?: boolean;
}

export const UnclaimedPrize = ({
  className,
  prizeWinId,
  prizeTierCode,
  prizeImageLocation,
  prizeType,
  drawName,
  drawId,
  prizeName,
  createdAt,
  expiresAt,
  drawPrizeSlug,
  onPrizeClaimed,
  showExpiry,
  consolationPrizeClaimButtonShowsPopup,
}: UnclaimedPrizeProps) => {
  const [showJackpotModal, setShowJackpotModal] = useState(false);
  const [showChoosePrizeModal, setShowChoosePrizeModal] = useState(false);
  const [prizeModalDetails, setPrizeModalDetails] = useState<null | {
    prizeDetails: TPrizeDetails;
    prizeType: 'prizeWin' | 'promoPrizeReward';
  }>(null);
  const { displayToast } = useUIStore();
  const { updateCurrentUser } = useUserStore();

  const expired = new Date(expiresAt) < new Date();
  const isMainDraw = prizeType === 'MAIN_DRAW';
  const isPromoPrize = prizeType === 'CONSOLATION_PRIZE';
  const isLeaderboardPrize = prizeType === 'LEADERBOARD_PRIZE';
  const isTreat = /^Treat\d+$/i.test(prizeTierCode);

  const displayName = isPromoPrize
    ? prizeName
    : isMainDraw
      ? getDisplayPrizeTierCode(prizeTierCode)
      : /^LEADERBOARD_RAFFLE/i.test(prizeTierCode)
        ? 'Leaderboard Raffle'
        : isLeaderboardPrize
          ? 'Leaderboard Draw'
          : drawName;

  const isJackpotWinner = prizeTierCode === 'DAYMAKERboost';

  const handleClaim = async () => {
    if (isJackpotWinner) {
      setShowJackpotModal(true);
      return;
    }

    if (prizeType === 'CONSOLATION_PRIZE') {
      if (consolationPrizeClaimButtonShowsPopup) {
        const resp = await winnerApiClient.auth.getPrizeBySlugForRedemption({
          query: {
            slug:
              drawPrizeSlug ||
              throwError(
                'Attempted to get prize for consolation prize with a falsey prize slug',
              ),
          },
        });

        if (resp.status === HttpStatusCode.OK) {
          setPrizeModalDetails({
            prizeDetails: resp.body,
            prizeType: 'promoPrizeReward',
          });
          setShowChoosePrizeModal(true);
        } else {
          displayToast({
            title: resp.body.message,
            color: '#FF6600',
            timeout: 5000,
          });
        }
        return;
      } else {
        return Router.push(`/my-wins#${prizeWinId}`);
      }
    }

    if (
      (prizeType === 'TOKEN_TOWN' || prizeType === 'LEADERBOARD_PRIZE') &&
      drawPrizeSlug
    ) {
      if (prizeModalDetails) {
        setShowChoosePrizeModal(true);
        return;
      }

      const resp = await winnerApiClient.auth.getPrizeBySlugForRedemption({
        query: { slug: drawPrizeSlug },
      });

      if (resp.status === HttpStatusCode.OK) {
        setPrizeModalDetails({
          prizeDetails: resp.body,
          prizeType: 'prizeWin',
        });
        setShowChoosePrizeModal(true);
      } else {
        displayToast({
          title: resp.body.message,
          color: '#FF6600',
          timeout: 5000,
        });
      }
      return;
    }

    if (isTreat && drawId) {
      return Router.push(`/my-wins/choose-treats?draw=${drawId}`);
    }

    Router.push(`/my-wins/redeem-prize?prizeWinId=${prizeWinId}`);
  };

  return (
    <>
      {showJackpotModal && (
        <GenericModal onClose={() => setShowJackpotModal(false)}>
          <div className={styles.modalTierIcon}>
            <ImageWithFallback
              layout="fill"
              objectFit="cover"
              src={getPrizeTierIcon(prizeTierCode, isLeaderboardPrize)}
            />
          </div>
          <h3 className="aboutUsTypographyHeaderMedium ">
            Claim your <br />
            {displayName} prize
          </h3>
          <p>
            As this is a high value prize, we'd love to chat through the next
            steps you'll need to take to claim it.
          </p>
          <p>
            Please email us at <strong>hello@daymade.co.uk</strong> to schedule
            a call!
          </p>
          <div style={{ width: '100%', marginTop: '25px' }}>
            <RoundedButton
              rebrandDm2025
              onClick={() => {
                window.location.href = 'mailto:hello@daymade.co.uk';
              }}
            >
              EMAIL US NOW
            </RoundedButton>
          </div>
        </GenericModal>
      )}
      {showChoosePrizeModal && prizeModalDetails && (
        <ChoosePrizeModal
          selectedPrize={prizeModalDetails.prizeDetails}
          sourceId={prizeWinId}
          sourceType={prizeModalDetails.prizeType}
          isChosenByPlayer={false}
          closePopup={() => setShowChoosePrizeModal(false)}
          onSuccessfulChoiceHandler={async () => {
            setShowChoosePrizeModal(false);
            onPrizeClaimed?.();
            updateCurrentUser();
          }}
        />
      )}
      <div
        className={
          className ? `${styles.container} ${className}` : styles.container
        }
      >
        <div className={styles.imageBackground}>
          <ImageWithFallback
            layout="fill"
            objectFit="cover"
            src={
              isPromoPrize
                ? getPrizeTierBackground('Treat1', prizeImageLocation)
                : getPrizeTierBackground(
                    prizeTierCode,
                    prizeImageLocation,
                    isLeaderboardPrize,
                  )
            }
          />
          {prizeType === 'MONTHLY_GIVEAWAY' && (
            <div className={styles.imageBackgroundOverlay} />
          )}
        </div>
        <div className={styles.overlay}>
          <div className={styles.tierIcon}>
            <ImageWithFallback
              layout="fill"
              objectFit="cover"
              src={
                isPromoPrize
                  ? getPrizeTierIcon('CONSOLATION')
                  : getPrizeTierIcon(prizeTierCode, isLeaderboardPrize)
              }
            />
          </div>
          <div className={styles.textSection}>
            <h3
              className={`aboutUsTypographyHeaderMedium textAlignLeft noMargin ${styles.title} ${isPromoPrize || isMainDraw ? '' : styles.textWhite}`}
            >
              You won a {displayName}
              {`${isPromoPrize || isTreat ? '' : ' prize'}`}!
            </h3>
            <p
              className={`aboutUsTypographyBodySmall
              noMargin ${isPromoPrize || isMainDraw ? '' : styles.textWhite}`}
            >
              Won:{' '}
              {new Date(createdAt)
                .toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: '2-digit',
                })
                .replace(/(\w{3})\s(\d{2})/, '$1, $2')}
            </p>
            {showExpiry ? (
              <p
                className={`aboutUsTypographyBodySmall
              noMargin ${isPromoPrize || isMainDraw ? '' : styles.textWhite}`}
              >
                {expired ? 'Expired' : 'Expires'}:{' '}
                {new Date(expiresAt)
                  .toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: '2-digit',
                  })
                  .replace(/(\w{3})\s(\d{2})/, '$1, $2')}
              </p>
            ) : null}
          </div>
          <div>
            <RoundedButton
              rebrandDm2025
              colorStyle={
                isPromoPrize || isMainDraw
                  ? ColorStyle.OutlineDark
                  : ColorStyle.OutlineWhite
              }
              mini
              disabled={expired}
              onClick={expired ? undefined : handleClaim}
            >
              {expired ? 'Expired' : 'CLAIM'}
            </RoundedButton>
          </div>
        </div>
      </div>
    </>
  );
};
